import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight, CreditCard, Truck, Check, Loader2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, cartTotal, cartId, clearLocalCart, API, BACKEND_URL } = useApp();
  const { token: customerToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
  });

  const deliveryCharge = cartTotal >= 500 ? 0 : 50;
  const total = cartTotal - couponDiscount + deliveryCharge;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    try {
      const response = await axios.post(`${API}/coupons/validate?code=${couponCode}&subtotal=${cartTotal}`);
      setCouponDiscount(response.data.discount_amount);
      setAppliedCoupon(response.data.code);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid coupon code");
      setCouponDiscount(0);
      setAppliedCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode("");
    setCouponDiscount(0);
    setAppliedCoupon(null);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const required = ["full_name", "phone", "address_line1", "city", "state", "pincode"];
    for (const field of required) {
      if (!formData[field].trim()) {
        toast.error(`Please fill in ${field.replace("_", " ")}`);
        return false;
      }
    }
    if (!/^\d{10}$/.test(formData.phone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return false;
    }
    if (!/^\d{6}$/.test(formData.pincode)) {
      toast.error("Please enter a valid 6-digit pincode");
      return false;
    }
    return true;
  };

  const handleProceedToPayment = () => {
    if (validateForm()) {
      setStep(2);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      // Create order
      const headers = { "Content-Type": "application/json" };
      if (customerToken) headers.Authorization = `Bearer ${customerToken}`;
      
      const orderResponse = await axios.post(`${API}/orders`, {
        cart_id: cartId,
        delivery_address: formData,
        coupon_code: appliedCoupon || null,
      }, { headers });

      const { order_id, razorpay_order_id, razorpay_key_id, amount } = orderResponse.data;

      // If Razorpay is configured, open payment
      if (razorpay_order_id && razorpay_key_id) {
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          toast.error("Failed to load payment gateway");
          setLoading(false);
          return;
        }

        const options = {
          key: razorpay_key_id,
          amount: amount,
          currency: "INR",
          name: "MiniBegumBazaar",
          description: "Order Payment",
          order_id: razorpay_order_id,
          handler: async (response) => {
            try {
              await axios.post(`${API}/orders/${order_id}/verify-payment`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              clearLocalCart();
              toast.success("Payment successful!");
              navigate(`/order-success/${order_id}`);
            } catch (error) {
              toast.error("Payment verification failed");
            }
          },
          prefill: {
            name: formData.full_name,
            contact: formData.phone,
          },
          theme: {
            color: "#C84B31",
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        // If Razorpay not configured, treat as COD
        clearLocalCart();
        toast.success("Order placed successfully!");
        navigate(`/order-success/${order_id}`);
      }
    } catch (error) {
      console.error("Order creation failed:", error);
      toast.error("Failed to create order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
          <Link to="/categories">
            <Button data-testid="start-shopping">Start Shopping</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="checkout-page">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="breadcrumb mb-8">
          <Link to="/">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/cart">Cart</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-foreground">Checkout</span>
        </nav>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-4">
            <div className="checkout-step">
              <div className={`checkout-step-number ${step >= 1 ? "active" : "inactive"}`}>
                {step > 1 ? <Check className="w-4 h-4" /> : "1"}
              </div>
              <span className={step >= 1 ? "font-medium" : "text-muted-foreground"}>Delivery</span>
            </div>
            <div className="w-12 h-0.5 bg-muted" />
            <div className="checkout-step">
              <div className={`checkout-step-number ${step >= 2 ? "active" : "inactive"}`}>
                2
              </div>
              <span className={step >= 2 ? "font-medium" : "text-muted-foreground"}>Payment</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {step === 1 && (
              <div className="bg-white rounded-xl border p-6" data-testid="delivery-form">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-xl font-bold">Delivery Address</h2>
                </div>

                <div className="grid gap-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="full_name">Full Name *</Label>
                      <Input
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        data-testid="input-full-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="10-digit mobile number"
                        maxLength={10}
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address_line1">Address Line 1 *</Label>
                    <Input
                      id="address_line1"
                      name="address_line1"
                      value={formData.address_line1}
                      onChange={handleInputChange}
                      placeholder="House/Flat No., Building Name, Street"
                      data-testid="input-address1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address_line2">Address Line 2</Label>
                    <Input
                      id="address_line2"
                      name="address_line2"
                      value={formData.address_line2}
                      onChange={handleInputChange}
                      placeholder="Landmark, Area (Optional)"
                      data-testid="input-address2"
                    />
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="City"
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="State"
                        data-testid="input-state"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pincode">Pincode *</Label>
                      <Input
                        id="pincode"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        placeholder="6-digit pincode"
                        maxLength={6}
                        data-testid="input-pincode"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleProceedToPayment}
                  className="w-full mt-6 btn-primary"
                  size="lg"
                  data-testid="proceed-to-payment"
                >
                  Continue to Payment
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="bg-white rounded-xl border p-6" data-testid="payment-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-xl font-bold">Payment</h2>
                </div>

                {/* Address Summary */}
                <div className="bg-muted p-4 rounded-lg mb-6">
                  <p className="text-sm text-muted-foreground mb-1">Delivering to:</p>
                  <p className="font-medium">{formData.full_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formData.address_line1}, {formData.address_line2 && `${formData.address_line2}, `}
                    {formData.city}, {formData.state} - {formData.pincode}
                  </p>
                  <p className="text-sm text-muted-foreground">Phone: {formData.phone}</p>
                  <Button
                    variant="link"
                    className="p-0 h-auto text-primary"
                    onClick={() => setStep(1)}
                    data-testid="edit-address"
                  >
                    Edit Address
                  </Button>
                </div>

                {/* Payment Options */}
                <div className="space-y-4">
                  <div className="border rounded-lg p-4 bg-primary/5 border-primary">
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full border-2 border-primary flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">Pay with Razorpay</p>
                        <p className="text-sm text-muted-foreground">
                          Cards, UPI, Net Banking, Wallets
                        </p>
                      </div>
                      <img src="https://razorpay.com/favicon.png" alt="Razorpay" className="h-8" />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handlePayment}
                  className="w-full mt-6 btn-primary"
                  size="lg"
                  disabled={loading}
                  data-testid="pay-now"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    `Pay ₹${total.toLocaleString()}`
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border p-6 sticky top-24" data-testid="checkout-summary">
              <h2 className="text-xl font-bold mb-6">Order Summary</h2>
              
              <div className="space-y-4 max-h-64 overflow-y-auto mb-4">
                {cart.items.map((item) => (
                  <div key={item.product_id} className="flex gap-3">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      <img
                        src={item.product?.images?.[0] || "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100"}
                        alt={item.product?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm line-clamp-2">{item.product?.name}</p>
                      <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium">
                      ₹{((item.product?.price || 0) * item.quantity).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              <hr className="my-4" />

              {/* Coupon Code */}
              <div className="mb-4">
                <p className="text-sm font-medium mb-2">Have a coupon?</p>
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200">
                    <div>
                      <span className="font-mono font-semibold text-green-700">{appliedCoupon}</span>
                      <span className="text-sm text-green-600 ml-2">-₹{couponDiscount}</span>
                    </div>
                    <button onClick={removeCoupon} className="text-red-500 text-sm hover:underline" data-testid="remove-coupon">Remove</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1"
                      data-testid="coupon-input"
                    />
                    <Button variant="outline" onClick={handleApplyCoupon} disabled={applyingCoupon} data-testid="apply-coupon-button">
                      {applyingCoupon ? "..." : "Apply"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">₹{cartTotal.toLocaleString()}</span>
                </div>
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Coupon Discount</span>
                    <span>-₹{couponDiscount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Delivery</span>
                  <span className="font-medium">
                    {deliveryCharge === 0 ? (
                      <span className="text-green-600">FREE</span>
                    ) : (
                      `₹${deliveryCharge}`
                    )}
                  </span>
                </div>
                <hr />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-primary">₹{total.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
